import { ErrorNotification, Input, PrimaryButton } from '@cb/apricot-react';
import { Grid, LinearProgress } from '@mui/material';
import { ChangeEventHandler, FC, FormEvent, useCallback, useState } from 'react';

import { IForgotPasswordVerificationForm } from 'src/types';
import { validateUserName } from 'src/utils';

interface ForgotPasswordVerificationFormProps {
  onSubmit: (form: IForgotPasswordVerificationForm) => void;
}

const ForgotPasswordVerificationForm: FC<ForgotPasswordVerificationFormProps> = ({ onSubmit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<Nullable<string>>(null);
  const [showFormStatus, setShowFormStatus] = useState(false);

  const [form, setForm] = useState<IForgotPasswordVerificationForm>({
    name: '',
  });

  const isValidName = validateUserName(form.name);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();

      if (!isValidName) {
        setShowFormStatus(true);
        return;
      }

      setIsLoading(true);
      try {
        await onSubmit(form);
        setErrorMessage(null);
      } catch (error: any) {
        setErrorMessage(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [form, isValidName, onSubmit],
  );

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && <LinearProgress color="secondary" />}
      {errorMessage && (
        // @ts-ignore
        <ErrorNotification dismissible title="Error">
          {errorMessage}
        </ErrorNotification>
      )}

      <Grid container spacing={3} sx={{ p: 3, px: 5 }}>
        <Grid item xs={12}>
          <Input
            name="name"
            label="Username/Email"
            placeholder="Username/Email"
            required
            value={form.name}
            validation={showFormStatus && !isValidName ? 'error' : undefined}
            validationMsg={!isValidName ? 'Invalid username, must contain at least 3 characters' : ''}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container alignItems="center">
            <PrimaryButton type="submit" style={{ width: '100%' }}>
              Send Verification Code
            </PrimaryButton>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default ForgotPasswordVerificationForm;
